'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA } from '@/app/components/blog/BlogAeo'
import RiskQuizInlineNudge from '@/app/components/blog/RiskQuizInlineNudge'
import KeyStats from '@/app/components/blog/KeyStats'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Does Dasani Have Microplastics? What's Really in the Bottle (2026)"
      author="MicroPlastics Research Desk"
      date="July 15, 2026"
      readTime="11 min read"
      heroImage="/blog/heroes/water.webp"
      heroImageAlt="Does Dasani have microplastics — the purified, remineralized PET bottled water audited against the research"
    >
      <BlogLastUpdated iso="2026-07-15" formatted="July 15, 2026" />

      <BlogTLDR>
        <strong>Yes — Dasani is purified tap water with minerals added back, sold
        in a PET plastic bottle, and it carries the same microplastic load as the
        bottled-water category.</strong>{' '}
        Coca-Cola makes Dasani by purifying municipal water through reverse osmosis,
        then adding magnesium sulfate, potassium chloride, and salt for taste. It
        was one of the eleven brands in the only named-brand study (Mason 2018),
        where <strong>93% of bottles were contaminated</strong>. No
        Dasani-specific particle count has been published, so treat any exact
        number with suspicion. But the mechanisms are well documented: the{' '}
        <strong>PET bottle sheds microplastics</strong> and the{' '}
        <strong>reverse-osmosis membrane sheds nanoplastics</strong> (PNAS 2024).
        The added minerals change the taste, not the plastic. The cleaner choice is{' '}
        <strong>filtered tap in glass or steel</strong>.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Dasani is <strong>Coca-Cola&apos;s purified municipal water</strong> with a mineral blend (magnesium sulfate, potassium chloride, salt) added for taste — bottled in <strong>PET (#1) plastic</strong>.</>,
          <>It was among the <strong>11 brands in Mason 2018</strong>, where 93% of 259 bottles across all brands showed microplastic contamination.</>,
          <>There is <strong>no published Dasani-specific microplastic count</strong>. We report the mechanisms and the category data instead of inventing a figure.</>,
          <>The <strong>added minerals are a flavour decision</strong>, not a filtration step — they do nothing to remove or prevent microplastics.</>,
          <>Two documented sources apply: the <strong>PET bottle</strong> (PNAS 2024 named PET as a top find) and the <strong>RO membrane</strong> (source of the most common plastic, polyamide).</>,
          <>The reliable fix is a <Link href="/blog/microplastics-water-filters-compared" className="text-forest underline">home filter</Link> into a <Link href="/blog/best-glass-water-bottles" className="text-forest underline">glass or steel bottle</Link> — not switching purified brands.</>,
        ]}
      />

      <RiskQuizInlineNudge source="blog_inline_dasani" />

      <KeyStats
        heading="Dasani & microplastics — the sourced facts"
        stats={[
          { value: '93%', label: 'of bottles contaminated (all brands)', context: 'Mason 2018 tested 259 bottles across 11 brands including Dasani', source: 'Mason et al. 2018, Frontiers in Chemistry', sourceUrl: 'https://www.frontiersin.org/articles/10.3389/fchem.2018.00407/full' },
          { value: '~240,000/L', label: 'avg particles in bottled water', context: 'across three unnamed brands; ~90% nanoplastics (PNAS 2024)', source: 'Qian et al. 2024, PNAS', sourceUrl: 'https://www.pnas.org/doi/10.1073/pnas.2300582121' },
          { value: 'PET #1', label: 'Dasani bottle material', context: 'the plastic PNAS 2024 found shedding into bottled water', source: 'Coca-Cola / Dasani packaging', sourceUrl: 'https://www.dasani.com/' },
          { value: '+minerals', label: 'magnesium sulfate, KCl, salt', context: 'added for taste after purification — not a microplastic filter', source: 'Dasani ingredient list', sourceUrl: 'https://www.dasani.com/' },
        ]}
      />

      <h2>Does Dasani contain microplastics?</h2>
      <p>
        Yes. Dasani was one of the eleven brands in the 2018 SUNY Fredonia study
        (Mason et al.), the only peer-reviewed research to name the brands it
        tested. Across all brands, <strong>93% of the 259 bottles showed
        microplastic contamination</strong>, averaging roughly 325 particles per
        litre once the smallest fragments were included.
      </p>
      <p>
        The published per-brand extremes were Nestlé Pure Life and Gerolsteiner
        (highest) and San Pellegrino and Minalba (lowest). Dasani sat within the
        tested group without being called out as a named high or low, so a
        Dasani-specific figure would be a fabrication. The defensible conclusion is
        the category one: contamination was near-universal.
      </p>

      <h2>Purified, then remineralized — does that help?</h2>
      <p>
        Dasani&apos;s recipe is what makes it distinct from Aquafina. Coca-Cola
        starts with municipal tap water, purifies it by reverse osmosis, then adds
        a small <strong>mineral blend</strong> — magnesium sulfate, potassium
        chloride, and salt — for the crisp taste some drinkers prefer (and others
        famously dislike).
      </p>
      <p>
        Here is the key point: <strong>none of that touches the plastic
        problem.</strong> The minerals are added for flavour after purification;
        they are not a filter and they do not bind or remove particles. Meanwhile
        the two documented plastic sources remain fully in play. The reverse-osmosis
        membrane is plastic — PNAS 2024 identified polyamide (nylon) from RO
        membranes as the most common nanoplastic in bottled water — and the
        finished product still ships in a <strong>PET bottle</strong> that sheds
        particles through heat and time. Remineralized purified water is still
        purified water in plastic.
      </p>

      <h2>Dasani vs the alternatives</h2>
      <BlogComparisonTable
        caption="Dasani vs everyday options, ranked by microplastic exposure"
        columns={[
          { key: 'option', label: 'Option' },
          { key: 'exposure', label: 'Microplastic exposure' },
          { key: 'note', label: 'Note' },
        ]}
        rows={[
          { option: 'Filtered tap → glass/steel bottle', exposure: 'Lowest', note: 'Add a pinch-of-mineral drop if you like Dasani’s taste' },
          { option: 'Home RO filter → non-plastic bottle', exposure: 'Lowest', note: 'Same purification Dasani uses, without the PET' },
          { option: 'Dasani (purified + minerals, PET)', exposure: 'High (bottled-water range)', note: 'RO membrane + PET bottle both contribute' },
          { option: 'Aquafina (purified, PET)', exposure: 'High', note: 'Same category; no minerals added' },
        ]}
      />
      <p>
        For the full ranking see our{' '}
        <Link href="/blog/microplastics-bottled-water-brands-ranked" className="text-forest underline">bottled water brands ranked</Link>{' '}
        and the{' '}
        <Link href="/data/bottled-water-brand-database" className="text-forest underline">bottled water microplastic database</Link>.
        Comparing the two big purified brands? Read the{' '}
        <Link href="/blog/does-aquafina-have-microplastics" className="text-forest underline">Aquafina audit</Link>, or see{' '}
        <Link href="/blog/tap-water-vs-bottled-water-microplastics" className="text-forest underline">tap vs bottled water</Link>. Storage matters too: here is what happens to{' '}<Link href="/blog/water-bottle-left-in-hot-car-microplastics" className="text-forest underline">bottled water left in a hot car</Link>.
      </p>

      <h2>What to drink instead of Dasani</h2>
      <ul>
        <li>
          <strong>Filter your tap water.</strong> A{' '}
          <Link href="/blog/microplastics-water-filters-compared" className="text-forest underline">reverse-osmosis or certified carbon-block filter</Link>{' '}
          replicates Dasani&apos;s purification at home without the PET bottle.
        </li>
        <li>
          <strong>Like the mineral taste?</strong> Add trace mineral drops to
          filtered water — the same idea Dasani uses — in a{' '}
          <Link href="/blog/best-glass-water-bottles" className="text-forest underline">glass bottle</Link>.
        </li>
        <li>
          <strong>Keep bottled for emergencies.</strong> Occasional Dasani is not
          the issue; a daily case of it is the higher-exposure, higher-cost habit.
        </li>
      </ul>

      <BlogAppCTA
        headline="Check your bottled water in seconds"
        sub="Scan your Dasani, your reusable bottle, or your filter pitcher with the MicroPlastics app to see the material, the exposure factors, and a cleaner swap."
      />

      <BlogFAQ
        items={[
          {
            question: 'Is Dasani just tap water?',
            answer: 'Largely, yes. Dasani is municipal tap water that Coca-Cola purifies by reverse osmosis and then remineralizes with magnesium sulfate, potassium chloride, and salt for taste. It is sourced from public water supplies, not a spring.',
          },
          {
            question: 'Do the minerals in Dasani remove microplastics?',
            answer: 'No. The magnesium sulfate, potassium chloride, and salt in Dasani are added after purification purely for taste. They do not filter, bind, or remove microplastic particles, and the water still ships in a PET plastic bottle that sheds plastic.',
          },
          {
            question: 'Is Dasani BPA-free?',
            answer: 'Dasani bottles are PET plastic, which does not use BPA. But BPA-free is not the same as microplastic-free: PET bottles shed microplastic particles, and purified bottled water showed near-universal contamination in published testing.',
          },
          {
            question: 'What water has fewer microplastics than Dasani?',
            answer: 'Filtered tap water in a glass or stainless steel bottle. A reverse-osmosis or certified carbon-block filter removes most microplastic particles, and a non-plastic bottle eliminates the PET shedding common to all bottled water including Dasani.',
          },
        ]}
      />

      <BlogSources
        items={[
          { authors: 'Mason SA, Welch VG, Neratko J', year: 2018, title: 'Synthetic Polymer Contamination in Bottled Water', publisher: 'Frontiers in Chemistry', url: 'https://www.frontiersin.org/articles/10.3389/fchem.2018.00407/full' },
          { authors: 'Qian N, et al.', year: 2024, title: 'Rapid single-particle chemical imaging of nanoplastics by SRS microscopy', publisher: 'PNAS', url: 'https://www.pnas.org/doi/10.1073/pnas.2300582121' },
          { authors: 'National Institutes of Health', year: 2024, title: 'Plastic particles in bottled water', publisher: 'NIH Research Matters', url: 'https://www.nih.gov/news-events/nih-research-matters/plastic-particles-bottled-water' },
        ]}
      />
    </BlogPostLayout>
  )
}
